import React, { useEffect, useMemo, useRef, useState } from 'react';
import { markStorySeen } from '../lib/stories';
import { nextAd } from '../lib/ads';
import EventDetailsDrawer from './EventDetailsDrawer';
import AdStorySlide from './AdStorySlide';
import AdDrawer from './AdDrawer';
import StoryImage from './StoryImage';

const STORY_DURATION = 6000;

function formatTime(time) {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
    });
}

function formatPrice(price) {
    if (!price) return '';
    if (price.amount === 0 || price.description === 'Free') return 'Free';
    if (price.amount > 0) return `$${price.amount} ${price.description || ''}`.trim();
    return price.description || '';
}

/**
 * The slide's date line. For a recurring event this leads with the soonest
 * upcoming date and notes how many dates remain, so it is clear the event
 * repeats without spending extra vertical space.
 */
function eventDateLine(event) {
    if (!event) return '';
    const date = `${formatDate(event.eventDate)}${event.start_time ? ` · ${formatTime(event.start_time)}` : ''}`;
    const totalDates = (event.nextSeriesEvents?.length || 0) + 1;
    if (totalDates > 1) return `Next: ${date} · ${totalDates} dates`;
    return date;
}

/**
 * StoriesViewer — full-screen story viewer. Iterates over business rings; each
 * business contributes up to five event slides with one pip per event at the
 * top (Instagram-style). A slide with a poster shows the poster (clean, top)
 * and the event details (bottom); without a poster, details use the full area.
 * Auto-advances and resets pips per business.
 */
export default function StoriesViewer({ stories, startIndex, onClose, analytics }) {
    // A "deck" interleaves sponsored ads after every 4 business story sets,
    // each dealt from the shared ad pool so no ad repeats before the whole
    // pool (feed ads included) has been shown.
    const deck = useMemo(() => {
        const items = [];
        let count = 0;
        for (const story of stories || []) {
            items.push({ type: 'story', business: story, slides: story.events || [] });
            count += 1;
            if (count % 4 === 0) {
                const ad = nextAd();
                if (ad) {
                    items.push({ type: 'ad', ad, slides: [{ eventId: `ad-${ad.businessId}` }] });
                }
            }
        }
        return items;
    }, [stories]);

    // Map the tapped business index into the deck (accounting for any ads that
    // landed before it), rather than assuming an arithmetic offset.
    const initialDeckIndex = (() => {
        const target = stories?.[startIndex]?.businessId;
        const i = deck.findIndex(
            (item) => item.type === 'story' && item.business.businessId === target
        );
        return i === -1 ? startIndex : i;
    })();

    const [deckIndex, setDeckIndex] = useState(initialDeckIndex);
    const [slideIndex, setSlideIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [paused, setPaused] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [adDrawerOpen, setAdDrawerOpen] = useState(false);
    const touchStartX = useRef(0);
    const progressTimer = useRef(null);

    const active = deck[deckIndex];
    const isAdSlide = active?.type === 'ad';
    const business = isAdSlide ? null : active?.business || null;
    const slides = active?.slides || [];
    const event = slides[slideIndex];

    // If the viewer advances while the ad drawer is open, close it so it never
    // lingers onto a later deck item or ad slide.
    useEffect(() => {
        if (!adDrawerOpen) return;
        setAdDrawerOpen(false);
        setPaused(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deckIndex, slideIndex]);

    // Mark seen once a business is active
    useEffect(() => {
        if (business) markStorySeen(business.businessId);
    }, [deckIndex, business]);

    // Progress tick
    useEffect(() => {
        setProgress(0);
        if (!event || paused || drawerOpen) return;
        progressTimer.current = setInterval(() => {
            setProgress((prev) => Math.min(100, prev + 1));
        }, STORY_DURATION / 100);
        return () => clearInterval(progressTimer.current);
    }, [event, paused, drawerOpen]);

    // Advance when complete
    useEffect(() => {
        if (progress >= 100 && !drawerOpen && !adDrawerOpen) advance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [progress, drawerOpen, adDrawerOpen]);

    function advance() {
        if (!active) return;
        const list = active.slides || [];
        if (slideIndex + 1 < list.length) {
            setSlideIndex(slideIndex + 1);
        } else if (deckIndex + 1 < deck.length) {
            // Reset the slide position in the same batch as the deck change.
            // A deferred reset lets the new set's pips mount at the stale
            // index and then animate ("drain") back to empty.
            setDeckIndex(deckIndex + 1);
            setSlideIndex(0);
        } else {
            onClose();
            return;
        }
        setProgress(0);
    }

    function goBack() {
        if (!active) return;
        if (slideIndex > 0) {
            setSlideIndex(slideIndex - 1);
        } else if (deckIndex > 0) {
            setDeckIndex(deckIndex - 1);
            setSlideIndex(0);
        }
        setProgress(0);
    }

    // Warm the browser cache for upcoming posters (rest of the current set,
    // then the next deck item's first slide) so slide transitions show the
    // new poster immediately instead of waiting on its fetch. Each URL is
    // requested at most once per viewing session; the Image objects are kept
    // referenced so in-flight fetches are not cancelled on re-render.
    const preloadedPosters = useRef(new Map());
    useEffect(() => {
        const upcoming = [
            ...slides.slice(slideIndex + 1).map((s) => s.poster),
            deck[deckIndex + 1]?.slides?.[0]?.poster,
        ].filter(Boolean);
        for (const url of upcoming) {
            if (preloadedPosters.current.has(url)) continue;
            const img = new Image();
            img.src = url;
            preloadedPosters.current.set(url, img);
        }
    }, [deckIndex, slideIndex, slides, deck]);

    // Keyboard navigation
    useEffect(() => {
        const keyHandler = (e) => {
            if (e.key === 'Escape') {
                if (drawerOpen) setDrawerOpen(false);
                else if (adDrawerOpen) setAdDrawerOpen(false);
                else onClose();
            }
            if (drawerOpen || adDrawerOpen) return;
            if (e.key === 'ArrowRight') advance();
            if (e.key === 'ArrowLeft') goBack();
        };
        document.addEventListener('keydown', keyHandler);
        return () => document.removeEventListener('keydown', keyHandler);
    });

    if (!deck || deck.length === 0) return null;

    // Body scroll lock
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    const hasPoster = Boolean(event?.poster);

    const businessHref = business
        ? business.isResource
            ? `/resources/#resource-${business.businessId}`
            : `/businesses/${business.businessId}/`
        : null;

    const openBusiness = (e) => {
        e.stopPropagation();
        analytics?.trackInteraction('click_story_business', { businessId: business?.businessId });
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black flex items-center justify-center" role="dialog" aria-modal="true" aria-label="Stories">
            {/* Phone-frame column (IG style): full width + full height on mobile, centered 9:16-ish on desktop */}
            <div className="relative w-full h-full max-w-[26.875rem] bg-black lg:my-4 lg:h-[calc(100vh-2rem)] lg:rounded-2xl lg:overflow-hidden">
                {/* Pips: one per event slide of the current business */}
                <div className="absolute top-0 inset-x-0 z-20 flex gap-1.5 px-3 pt-3">
                    {slides.map((slide, si) => (
                        <div key={slide.eventId} className="flex-1 h-1 rounded-full bg-white/30 overflow-hidden">
                            <div
                                className="h-full bg-white transition-[width] duration-150 ease-linear"
                                style={{ width: si < slideIndex ? '100%' : si === slideIndex ? `${progress}%` : '0%' }}
                            />
                        </div>
                    ))}
                </div>

                {/* Slide content */}
                <div
                    className="absolute inset-0"
                    onMouseDown={() => setPaused(true)}
                    onMouseUp={() => setPaused(false)}
                    onMouseLeave={() => setPaused(false)}
                    onTouchStart={(e) => {
                        touchStartX.current = e.touches[0].clientX;
                        setPaused(true);
                    }}
                    onTouchEnd={(e) => {
                        setPaused(false);
                        const diff = touchStartX.current - e.changedTouches[0].clientX;
                        if (Math.abs(diff) > 50) {
                            if (diff > 0) advance(); else goBack();
                        }
                    }}
                    onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        if (x < rect.width / 2) { goBack(); setPaused(true); }
                        else { advance(); setPaused(true); }
                        setTimeout(() => setPaused(false), 300);
                    }}
                >
                    {isAdSlide ? (
                        <AdStorySlide
                            ad={active.ad}
                            analytics={analytics}
                            onOpen={() => {
                                setPaused(true);
                                setAdDrawerOpen(true);
                            }}
                        />
                    ) : hasPoster ? (
                        /* Split slide: poster on top (clean, no covering), details below.
                           One gradient overlays the whole slide so there is no seam. */
                        <div className="absolute inset-0 flex flex-col bg-gray-950">
                            <div className="absolute inset-0 bg-gradient-to-br from-brand-900/40 to-brand-900/40 pointer-events-none" />
                            <div className="relative w-full flex-[2] min-h-0 flex items-center justify-center">
                                <StoryImage key={event.eventId} src={event.poster} alt={event?.title || business.businessTitle} className="max-h-full max-w-full object-contain" />
                            </div>
                            <div className="relative w-full flex-1 min-h-0 overflow-y-auto">
                                <div className="relative p-5 pb-8 text-white h-full flex flex-col">
                                    {/* Business head */}
                                    <a
                                        href={businessHref}
                                        onClick={openBusiness}
                                        className="flex items-center gap-3 mb-3 no-underline text-white hover:opacity-90 transition-opacity"
                                    >
                                        {business.avatarUrl ? (
                                            <img src={business.avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white/70" />
                                        ) : null}
                                        <div className="min-w-0">
                                            <p className="font-semibold leading-tight truncate text-sm">{business.businessTitle}</p>
                                            {event && (
                                                <p className="text-xs text-white/70 truncate">{eventDateLine(event)}</p>
                                            )}
                                        </div>
                                    </a>

                                    {event ? (
                                        <>
                                            <h3 className="text-lg font-bold leading-snug mb-2">{event.title}</h3>

                                            <div className="flex flex-wrap gap-2 mb-3">
                                                {formatPrice(event.price) && (
                                                    <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-white/15 rounded-full">{formatPrice(event.price)}</span>
                                                )}
                                                {event.over21 && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-white/15 rounded-full">21+</span>}
                                                {event.over18 && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-white/15 rounded-full">18+</span>}
                                                {event.virtual && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-emerald-500 rounded-full">Virtual</span>}
                                                {event.soldOut && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-red-500 rounded-full">Sold Out</span>}
                                                {event.goingFast && !event.soldOut && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-orange-500 rounded-full">Going Fast</span>}
                                            </div>

                                            {event.description && (
                                                <p className="text-sm text-white/85 leading-relaxed line-clamp-3 mb-4">{event.description}</p>
                                            )}

                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPaused(true);
                                                    setDrawerOpen(true);
                                                    analytics?.trackInteraction('click_story_details');
                                                }}
                                                className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 text-white text-sm font-semibold px-5 py-2.5 hover:bg-brand-700 transition-colors"
                                            >
                                                Details
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                </svg>
                                            </button>
                                        </>
                                    ) : (
                                        <p className="text-white/85 text-sm">No upcoming events.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Info-only slide background */}
                            <div className="absolute inset-0 bg-black">
                                {business.heroUrl || business.avatarUrl ? (
                                    <StoryImage key={business.businessId} src={business.heroUrl || business.avatarUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                ) : (
                                    <div className="absolute inset-0 bg-gradient-to-br from-brand-500 to-brand-700" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/95 from-0% via-black/60 via-45% to-transparent to-75%" />
                            </div>

                            {/* Overlay content */}
                            <div className="absolute inset-x-0 bottom-0 z-10 p-6 pt-16 pb-10 text-white bg-gradient-to-t from-black/85 to-transparent">
                                {/* Business head */}
                                <a
                                    href={businessHref}
                                    onClick={openBusiness}
                                    className="flex items-center gap-3 mb-3 no-underline text-white hover:opacity-90 transition-opacity"
                                >
                                    {business.avatarUrl ? (
                                        <img src={business.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-white/70" />
                                    ) : null}
                                    <div className="min-w-0">
                                        <p className="font-semibold leading-tight truncate">{business.businessTitle}</p>
                                        {event && (
                                            <p className="text-sm text-white/70 truncate">{eventDateLine(event)}</p>
                                        )}
                                    </div>
                                </a>

                                {event ? (
                                    <>
                                        <h3 className="text-xl font-bold leading-snug mb-2">{event.title}</h3>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {formatPrice(event.price) && (
                                            <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-white/15 rounded-full">{formatPrice(event.price)}</span>
                                            )}
                                            {event.over21 && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-white/15 rounded-full">21+</span>}
                                            {event.over18 && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-white/15 rounded-full">18+</span>}
                                            {event.virtual && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-emerald-500 rounded-full">Virtual</span>}
                                            {event.soldOut && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-red-500 rounded-full">Sold Out</span>}
                                            {event.goingFast && !event.soldOut && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-orange-500 rounded-full">Going Fast</span>}
                                        </div>

                                        {event.description && (
                                            <p className="text-sm text-white/85 leading-relaxed line-clamp-2 mb-4">{event.description}</p>
                                        )}

                                        <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPaused(true);
                                                    setDrawerOpen(true);
                                                    analytics?.trackInteraction('click_story_details');
                                                }}
                                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 text-white text-sm font-semibold px-5 py-2.5 hover:bg-brand-700 transition-colors"
                                            >
                                                Details
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                </svg>
                                            </button>
                                    </>
                                ) : (
                                    <p className="text-white/85 text-sm">No upcoming events.</p>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Story details drawer */}
                {drawerOpen && !isAdSlide && (
                    <EventDetailsDrawer
                        business={business}
                        event={event}
                        analytics={analytics}
                        onClose={() => setDrawerOpen(false)}
                        position="absolute"
                        trackNs="story"
                    />
                )}

                {/* Ad details drawer */}
                {adDrawerOpen && isAdSlide && (
                    <AdDrawer
                        ad={active.ad}
                        analytics={analytics}
                        position="absolute"
                        onClose={() => {
                            setAdDrawerOpen(false);
                            setPaused(false);
                        }}
                    />
                )}

                {/* Close */}
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 z-20 p-2 rounded-lg text-white hover:bg-white/20 transition-colors"
                    style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.9)) drop-shadow(0 4px 12px rgba(0,0,0,0.7))' }}
                    aria-label="Close stories"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>
        </div>
    );
}